import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { 
  FaUser, 
  FaWeight, 
  FaRulerVertical, 
  FaCalendarAlt, 
  FaSearch,
  FaRunning,
  FaUtensils,
  FaChartLine,
  FaPlusCircle,
  FaEllipsisH,
  FaFilter,
  FaPause,
  FaPlay
} from 'react-icons/fa';

const PageContainer = styled.div`
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  margin-bottom: 0.75rem;
  font-size: 1.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 3px;
  }
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  max-width: 600px;
  line-height: 1.5;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background: white;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex-grow: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(41, 23, 89, 0.1);
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  
  @media (min-width: 768px) {
    min-width: 120px;
  }
`;

const AddClientButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    min-width: 150px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 1.5rem 0;
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 2rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ClientCard = styled(Card)`
  height: 100%;
  transition: all 0.3s;
  border-radius: 12px;
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }
`;

const ClientHeader = styled(Card.Header)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ClientAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const ClientInfo = styled.div`
  flex: 1;
`;

const ClientName = styled.h3`
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ClientEmail = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const ClientContent = styled(Card.Content)`
  padding: 1.25rem;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tag = styled.span`
  background-color: ${({ theme, bgColor }) => bgColor || theme.colors.primaryLight};
  color: ${({ theme, color }) => color || theme.colors.primary};
  padding: 0.35rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const ClientStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.35rem;
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CardFooter = styled(Card.Footer)`
  padding: 1rem 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
`;

const ViewButton = styled(Button)`
  width: 100%;
`;

const MoreButton = styled.button`
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  &:hover {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 10;
  min-width: 180px;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border: none;
  background: white;
  cursor: pointer;
  color: ${props => props.textColor || props.theme.colors.textPrimary};
  
  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const PlanStatusTag = styled.div`
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background-color: ${props => props.isPaused ? '#fef3c7' : '#dcfce7'};
  color: ${props => props.isPaused ? '#d97706' : '#16a34a'};
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const ClientManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [success, setSuccess] = useState('');
  
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Get all client references for this partner
        const clientsCollectionRef = collection(db, 'partners', currentUser.uid, 'clients');
        const clientsSnapshot = await getDocs(clientsCollectionRef);
        
        // Fetch complete client data and health profiles
        const clientsData = await Promise.all(
          clientsSnapshot.docs.map(async (clientDoc) => {
            const clientId = clientDoc.id;
            const clientRef = doc(db, 'users', clientId);
            const clientSnapshot = await getDoc(clientRef);
            
            let healthData = null;
            // Try to get health profile data
            try {
              const healthRef = doc(db, 'users', clientId, 'health', 'profile');
              const healthSnapshot = await getDoc(healthRef);
              
              if (healthSnapshot.exists()) {
                healthData = healthSnapshot.data();
              }
            } catch (err) {
              console.error(`Error fetching health data for client ${clientId}:`, err);
            }
            
            if (clientSnapshot.exists()) {
              return {
                id: clientId,
                ...clientSnapshot.data(),
                health: healthData,
                assignedAt: clientDoc.data().assignedAt
              };
            }
            
            return null;
          })
        );
        
        // Filter out any null values (clients that might have been deleted)
        const validClients = clientsData.filter(client => client !== null);
        
        setClients(validClients);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load clients');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClients();
  }, [currentUser]);
  
  // Format health goals for display
  const formatHealthGoal = (goal) => {
    if (!goal) return null;
    
    const goals = {
      'weight-loss': 'Weight Loss',
      'muscle-gain': 'Muscle Gain',
      'maintenance': 'Weight Maintenance',
      'health-improvement': 'General Health',
      'athletic-performance': 'Athletic Performance'
    };
    
    return goals[goal] || goal;
  };
  
  // Format activity level for display
  const formatActivityLevel = (level) => {
    if (!level) return null;
    
    const levels = {
      'sedentary': 'Sedentary',
      'light': 'Lightly Active',
      'moderate': 'Moderately Active',
      'very': 'Very Active',
      'extra': 'Extra Active'
    };
    
    return levels[level] || level;
  };
  
  // Get client initials for avatar
  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (client.displayName && client.displayName.toLowerCase().includes(term)) ||
      (client.email && client.email.toLowerCase().includes(term)) ||
      (client.health?.dietaryRestrictions && client.health.dietaryRestrictions.toLowerCase().includes(term))
    );
  });
  
  // Add function to handle plan pause/resume
  const handleTogglePlanStatus = async (clientId, pauseAction) => {
    try {
      // Close dropdown
      setActiveDropdown(null);
      
      const clientRef = doc(db, 'users', clientId);
      const clientDoc = await getDoc(clientRef);
      
      if (!clientDoc.exists()) {
        setError('Client not found');
        return;
      }
      
      const clientData = clientDoc.data();
      
      if (pauseAction) {
        // Pause the plan
        await updateDoc(clientRef, {
          isPlanPaused: true,
          planPausedAt: serverTimestamp()
        });
        setSuccess(`Plan paused for ${clientData.displayName || 'client'}. End date will be extended when resumed.`);
      } else {
        // Resume the plan
        // Calculate additional paused days to extend the plan's end date
        let additionalPausedDays = 0;
        
        if (clientData.planPausedAt) {
          const pauseDate = clientData.planPausedAt.toDate ? 
            clientData.planPausedAt.toDate() : new Date(clientData.planPausedAt);
          const now = new Date();
          additionalPausedDays = Math.floor((now - pauseDate) / (1000 * 60 * 60 * 24));
        }
        
        await updateDoc(clientRef, {
          isPlanPaused: false,
          planPausedAt: null,
          planPausedDays: (clientData.planPausedDays || 0) + additionalPausedDays
        });
        setSuccess(`Plan resumed for ${clientData.displayName || 'client'}. End date extended by ${additionalPausedDays} day${additionalPausedDays !== 1 ? 's' : ''}.`);
      }
      
      // Update local state
      const updatedClients = clients.map(client => {
        if (client.id === clientId) {
          return {
            ...client,
            isPlanPaused: pauseAction,
            planPausedAt: pauseAction ? new Date() : null
          };
        }
        return client;
      });
      
      setClients(updatedClients);
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
      
    } catch (err) {
      console.error('Error updating plan status:', err);
      setError('Failed to update plan status');
      
      // Clear error message after a few seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    }
  };
  
  return (
    <PageContainer>
      <Header>
        <Title>Client Management</Title>
        <Subtitle>View and manage your clients, track their progress, and create personalized nutrition plans.</Subtitle>
      </Header>
      
      <FiltersContainer>
        <SearchContainer>
          <SearchIcon />
          <SearchInput 
            type="text" 
            placeholder="Search clients..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterButton variant="outline">
          <FaFilter />
          Filter
        </FilterButton>
        
        <AddClientButton>
          <FaPlusCircle />
          Add Client
        </AddClientButton>
      </FiltersContainer>
      
      {success && (
        <SuccessMessage>
          {success}
        </SuccessMessage>
      )}
      
      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}
      
      {loading && (
        <LoadingState>
          <h3>Loading clients...</h3>
          <p>Please wait while we fetch your client data.</p>
        </LoadingState>
      )}
      
      {!loading && !error && filteredClients.length === 0 && (
        <EmptyState>
          <h3>No Clients Found</h3>
          <p>
            {searchTerm ? 
              'No clients match your search criteria. Try adjusting your filters.' : 
              'You don\'t have any clients assigned to you yet.'}
          </p>
        </EmptyState>
      )}
      
      {!loading && !error && filteredClients.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>Found {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}</div>
          </div>
          
          <ClientsGrid>
            {filteredClients.map(client => (
              <ClientCard key={client.id}>
                <ClientHeader>
                  <ClientAvatar>
                    {getInitials(client.displayName)}
                  </ClientAvatar>
                  <ClientInfo>
                    <ClientName>{client.displayName || 'Client'}</ClientName>
                    <ClientEmail>{client.email}</ClientEmail>
                  </ClientInfo>
                </ClientHeader>
                
                <ClientContent>
                  <TagsContainer>
                    {client.health?.healthGoals && (
                      <Tag>
                        <FaChartLine />
                        {formatHealthGoal(client.health.healthGoals)}
                      </Tag>
                    )}
                    {client.health?.activityLevel && (
                      <Tag bgColor="#e6f2ff" color="#0066cc">
                        <FaRunning />
                        {formatActivityLevel(client.health.activityLevel)}
                      </Tag>
                    )}
                    {client.health?.dietaryRestrictions && (
                      <Tag bgColor="#fff2e6" color="#ff8c1a">
                        <FaUtensils />
                        {client.health.dietaryRestrictions}
                      </Tag>
                    )}
                    
                    {/* Show plan status if a plan exists */}
                    {client.selectedPlanId && (
                      <PlanStatusTag isPaused={client.isPlanPaused}>
                        {client.isPlanPaused ? (
                          <>
                            <FaPause /> Plan Paused
                          </>
                        ) : (
                          <>
                            <FaPlay /> Plan Active
                          </>
                        )}
                      </PlanStatusTag>
                    )}
                  </TagsContainer>
                  
                  <ClientStats>
                    {client.health?.age && (
                      <StatItem>
                        <StatLabel>Age</StatLabel>
                        <StatValue>{client.health.age} years</StatValue>
                      </StatItem>
                    )}
                    
                    {client.health?.gender && (
                      <StatItem>
                        <StatLabel>Gender</StatLabel>
                        <StatValue>{client.health.gender}</StatValue>
                      </StatItem>
                    )}
                    
                    {client.health?.height && (
                      <StatItem>
                        <StatLabel>
                          <FaRulerVertical />
                          Height
                        </StatLabel>
                        <StatValue>{client.health.height} cm</StatValue>
                      </StatItem>
                    )}
                    
                    {client.health?.weight && (
                      <StatItem>
                        <StatLabel>
                          <FaWeight />
                          Weight
                        </StatLabel>
                        <StatValue>{client.health.weight} kg</StatValue>
                      </StatItem>
                    )}
                  </ClientStats>
                  
                  <Divider />
                  
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
                    Client since: {client.assignedAt ? new Date(client.assignedAt.toDate()).toLocaleDateString() : 'N/A'}
                  </div>
                </ClientContent>
                
                <CardFooter>
                  <ViewButton 
                    onClick={() => navigate(`/partner/clients/${client.id}`)} 
                    variant="primary"
                  >
                    View Details
                  </ViewButton>
                  
                  <div style={{ position: 'relative' }}>
                    <MoreButton onClick={() => setActiveDropdown(activeDropdown === client.id ? null : client.id)}>
                      <FaEllipsisH />
                    </MoreButton>
                    
                    {activeDropdown === client.id && (
                      <Dropdown>
                        {client.selectedPlanId && (
                          client.isPlanPaused ? (
                            <DropdownItem 
                              onClick={() => handleTogglePlanStatus(client.id, false)}
                              textColor="#16a34a"
                            >
                              <FaPlay /> Resume Plan
                            </DropdownItem>
                          ) : (
                            <DropdownItem 
                              onClick={() => handleTogglePlanStatus(client.id, true)}
                              textColor="#d97706"
                            >
                              <FaPause /> Pause Plan
                            </DropdownItem>
                          )
                        )}
                      </Dropdown>
                    )}
                  </div>
                </CardFooter>
              </ClientCard>
            ))}
          </ClientsGrid>
        </>
      )}
    </PageContainer>
  );
};

export default ClientManagement; 